import React, { useState, useEffect } from 'react';
import { 
    Container, Grid, Typography, 
    Box, CircularProgress, FormControl, InputLabel, Select, MenuItem, Paper
} from '@mui/material';
import PortfolioList from './PortfolioList';
import OrdersTable from './OrdersTable';
import { EXCHANGE_FEE_RATES } from '../../constants/exchange';

/**
 * 주문 분석 컴포넌트
 * 
 * @returns {JSX.Element} 주문 분석 컴포넌트
 */
const OrderAnalytics = () => {
    const [timeRange, setTimeRange] = useState('today');  // 기본값을 오늘로 설정
    const [portfolios, setPortfolios] = useState([]);
    const [analytics, setAnalytics] = useState({ 
        totalPnL: 0, 
        totalKrwChange: 0, 
        totalUsdtChange: 0, 
        totalVolumes: { upbit: 0, bithumb: 0, binancef: 0, bybit: 0 }, 
        portfolioPnL: {}, 
        orderPnL: {} 
    });
    const [selectedPortfolio, setSelectedPortfolio] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const multiplier = timeRange === '31days' ? 3 : 1;

    const timeRangeOptions = [
        // KST 9시 기준 (UTC 0시)
        { value: 'today', label: '오늘 (KST 9시~)' },
        { value: '1days', label: '1일 전부터 (KST 9시~)' },
        { value: '2days', label: '2일 전부터 (KST 9시~)' },
        { value: '3days', label: '3일 전부터 (KST 9시~)' },
        { value: '5days', label: '5일 전부터 (KST 9시~)' },
        { value: '7days', label: '7일 전부터 (KST 9시~)' },
        { value: '30days', label: '30일 전부터 (KST 9시~)' },
        { value: '31days', label: '31일 전부터 (KST 9시~)' },
        // 최근 N시간
        { value: '1', label: '최근 1시간' },
        { value: '4', label: '최근 4시간' },
        { value: '8', label: '최근 8시간' },
        { value: '16', label: '최근 16시간' },
        { value: '24', label: '최근 24시간' },
        { value: '72', label: '최근 72시간' },
        { value: '10000', label: '전체' },

    ];

    const getTimeRange = (range) => {
        const now = new Date();
        
        // KST 9시 기준 옵션들
        if (range === 'today' || range.endsWith('days')) {
            const todayStart = new Date(now);
            todayStart.setUTCHours(0, 0, 0, 0);  // UTC 0시 = KST 9시

            if (range === 'today') {
                return { start: todayStart, end: now };
            }

            const days = parseInt(range);
            if (!isNaN(days)) {
                const start = new Date(todayStart);
                start.setDate(start.getDate() - days);
                return { start, end: now };
            }
        }
        
        // 시간 단위 옵션
        const hours = parseInt(range);
        if (!isNaN(hours)) {
            const start = new Date(now);
            start.setHours(now.getHours() - hours);
            return { start, end: now };
        }

        return { start: new Date(now.getTime() - 24 * 60 * 60 * 1000), end: now };
    };

    const fetchPortfolioData = async () => {
        setLoading(true);
        setError(null);
        try {
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const range = getTimeRange(timeRange);
            
            // 1일씩 나누어 요청 (최신 데이터부터)
            const startDate = range.start;
            const endDate = range.end;
            const DAYS_PER_REQUEST = 1;
            
            let allData = [];
            let currentEnd = endDate;
            
            // 환경변수와 URL 로깅
            console.log('Environment & Request:', {
                REACT_APP_API_URL: process.env.REACT_APP_API_URL,
                baseUrl,
                fullUrl: `${baseUrl}/portfolio/portfolio-analysis/range`,
                NODE_ENV: process.env.NODE_ENV
            });
            
            while (currentEnd > startDate) {
                let currentStart = new Date(currentEnd);
                currentStart.setDate(currentEnd.getDate() - DAYS_PER_REQUEST);
                if (currentStart < startDate) {
                    currentStart = startDate;
                }

                const url = `${baseUrl}/portfolio/portfolio-analysis/range?startTime=${currentStart.toISOString()}&endTime=${currentEnd.toISOString()}`;
                console.log('Request:', { timeRange, range: { start: currentStart, end: currentEnd }, url });
                
                const response = await fetch(url);
                
                // 응답 상태 로깅
                console.log('Response Status:', {
                    status: response.status,
                    statusText: response.statusText,
                    headers: Object.fromEntries(response.headers)
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.log('Error Response Text:', errorText);  // 에러 응답 내용 확인
                    throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
                }

                const partialData = await response.json();
                allData = [...allData, ...partialData];
                currentEnd = currentStart;
                
                console.log(`[진행] ${currentStart.toISOString()} ~ ${currentEnd.toISOString()} 데이터 로드 완료`);
            }

            const data = allData;
            console.log('Response Data:', {
                dataType: typeof data,
                isArray: Array.isArray(data),
                length: Array.isArray(data) ? data.length : 0
            });
            console.log('Response Data:', data);

            // 백엔드 응답 데이터 구조
            // {
            //   [portfolio_id: string]: {
            //     [order_id: string]: {
            //       korea_order: Object,    // 국내 거래소 주문 정보
            //       future_order: Object,   // 해외 거래소 주문 정보
            //       pnl: {
            //         krw_change: number,      // 국내 거래소 KRW 변화
            //         usdt_change: number,     // 해외 거래소 USDT 변화
            //         krw_from_usdt: number,   // USDT를 KRW로 환산한 값
            //         total: number,           // 총 PnL
            //         target_premium: number,  // 목표 프리미엄
            //         executed_premium: number, // 실제 체결된 프리미엄
            //         index_premium: number,   // 기준 프리미엄
            //         base_usdt: number,       // 기준 USDT 가격
            //         volumes: {               // 거래량 정보
            //           upbit: number,         // 업비트 거래량
            //           bithumb: number,       // 빗썸 거래량
            //           binancef: number,      // 바이낸스 거래량
            //           bybit: number          // 바이빗 거래량
            //         }
            //       }
            //     }
            //   }
            // }

            setPortfolios(data || []);

            // 전체 실현 손익 계산
            const totalPnL = (data || []).reduce((total, portfolio) => {
                return total + portfolio.orders.reduce((portfolioTotal, order) => {
                    return portfolioTotal + ((order?.pnl?.total || 0) * multiplier);
                }, 0);
            }, 0);

            // KRW 변화량 합계
            const totalKrwChange = (data || []).reduce((total, portfolio) => {
                return total + portfolio.orders.reduce((portfolioTotal, order) => {
                    return portfolioTotal + ((order?.pnl?.krw_change || 0) * multiplier);
                }, 0);
            }, 0);

            // USDT 변화량 합계
            const totalUsdtChange = (data || []).reduce((total, portfolio) => {
                return total + portfolio.orders.reduce((portfolioTotal, order) => {
                    return portfolioTotal + ((order?.pnl?.usdt_change || 0) * multiplier);
                }, 0);
            }, 0);

            // 거래소별 거래량 합계
            const totalVolumes = (data || []).reduce((total, portfolio) => {
                return portfolio.orders.reduce((portfolioTotal, order) => {
                    return {
                        upbit: portfolioTotal.upbit + (order?.pnl?.volumes?.upbit || 0),
                        bithumb: portfolioTotal.bithumb + (order?.pnl?.volumes?.bithumb || 0),
                        binancef: portfolioTotal.binancef + (order?.pnl?.volumes?.binancef || 0),
                        bybit: portfolioTotal.bybit + (order?.pnl?.volumes?.bybit || 0)
                    };
                }, total);
            }, { upbit: 0, bithumb: 0, binancef: 0, bybit: 0 });

            setAnalytics({
                totalPnL,
                totalKrwChange,
                totalUsdtChange,
                totalVolumes,
                portfolioPnL: {},
                orderPnL: {}
            });
            
            console.log('Total KRW Change:', Math.round(totalKrwChange).toLocaleString(), 'KRW');
            console.log('Total USDT Change:', (Math.round(totalUsdtChange * 100) / 100).toLocaleString(), 'USDT');
            console.log('Total PnL:', Math.round(totalPnL).toLocaleString(), 'KRW');
            console.log('Total Volumes:', {
                'Upbit': Math.round(totalVolumes.upbit).toLocaleString() + ' KRW',
                'Bithumb': Math.round(totalVolumes.bithumb).toLocaleString() + ' KRW',
                'Binancef': (Math.round(totalVolumes.binancef * 100) / 100).toLocaleString() + ' USDT',
                'Bybit': (Math.round(totalVolumes.bybit * 100) / 100).toLocaleString() + ' USDT'
            });

            if (!selectedPortfolio && data.length > 0) {
                setSelectedPortfolio(data[0].portfolioId);
            }
        } catch (err) {
            console.error('Error details:', {
                name: err.name,
                message: err.message,
                stack: err.stack,
                type: typeof err
            });
            setError(`Failed to fetch data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortfolioData();
    }, [timeRange]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" sx={{ p: 2 }}>
                {error}
            </Typography>
        );
    }

    return (
        <Container maxWidth={false} sx={{ height: '100%', py: 2 }}>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3, 
                height: '100%',
                maxHeight: { xs: 'none', md: 'calc(100vh - 32px)' }
            }}>
                <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    width: { xs: '100%', md: '250px' }
                }}>
                    <FormControl size="small" sx={{ width: '100%', mb: 2 }}>
                        <InputLabel>조회 기간</InputLabel>
                        <Select
                            value={timeRange}
                            onChange={(e) => setTimeRange(e.target.value)}
                            label="조회 기간"
                        >
                            {timeRangeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <PortfolioList 
                        portfolios={portfolios}
                        analytics={analytics}
                        selectedPortfolio={selectedPortfolio}
                        onPortfolioSelect={setSelectedPortfolio}
                    />
                </Box>

                <Box sx={{ 
                    flex: 1, 
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '100%'
                }}>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, color: '#666' }}>
                                    전체 실현 손익
                                </Typography>
                                <Typography 
                                    variant="h6"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: analytics.totalPnL >= 0 ? '#2e7d32' : '#d32f2f'
                                    }}
                                >
                                    ₩{Math.round(analytics.totalPnL).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
                                    원화: ₩{Math.round(analytics.totalKrwChange).toLocaleString()} / 
                                    달러: ${(Math.round(analytics.totalUsdtChange * 100) / 100).toLocaleString()}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#666', mt: 2, pt: 1, borderTop: '1px solid #ddd' }}>
                                    총 수수료: ₩{Math.round((analytics.totalVolumes.upbit * EXCHANGE_FEE_RATES.UPBIT + analytics.totalVolumes.bithumb * EXCHANGE_FEE_RATES.BITHUMB) * multiplier).toLocaleString()} / ${(Math.round((analytics.totalVolumes.binancef * EXCHANGE_FEE_RATES.BINANCEF + analytics.totalVolumes.bybit * EXCHANGE_FEE_RATES.BYBIT) * multiplier * 100) / 100).toLocaleString()}
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                                <Grid container sx={{ mb: 0.5, color: '#666', borderBottom: '1px solid #ddd', pb: 0.5 }}>
                                    <Grid item xs={3} md={2}><Typography variant="subtitle2" fontWeight="bold">거래소</Typography></Grid>
                                    <Grid item xs={4} md={3}><Typography variant="subtitle2" fontWeight="bold">거래량</Typography></Grid>
                                    <Grid item xs={0} md={2}><Typography variant="subtitle2" fontWeight="bold" sx={{ display: { xs: 'none', md: 'block' } }}>수수료율</Typography></Grid>
                                    <Grid item xs={5} md={5}><Typography variant="subtitle2" fontWeight="bold">수수료</Typography></Grid>
                                </Grid>
                                <Grid container sx={{ color: '#666', mb: 0.5 }}>
                                    <Grid item xs={3} md={2}><Typography variant="body2">Upbit</Typography></Grid>
                                    <Grid item xs={4} md={3}><Typography variant="body2">₩{Math.round(analytics.totalVolumes.upbit).toLocaleString()}</Typography></Grid>
                                    <Grid item xs={0} md={2}><Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>{EXCHANGE_FEE_RATES.UPBIT * 100}%</Typography></Grid>
                                    <Grid item xs={5} md={5}><Typography variant="body2">₩{Math.round(analytics.totalVolumes.upbit * EXCHANGE_FEE_RATES.UPBIT).toLocaleString()}</Typography></Grid>
                                </Grid>
                                <Grid container sx={{ color: '#666', mb: 0.5 }}>
                                    <Grid item xs={3} md={2}><Typography variant="body2">Bithumb</Typography></Grid>
                                    <Grid item xs={4} md={3}><Typography variant="body2">₩{Math.round(analytics.totalVolumes.bithumb).toLocaleString()}</Typography></Grid>
                                    <Grid item xs={0} md={2}><Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>{EXCHANGE_FEE_RATES.BITHUMB * 100}%</Typography></Grid>
                                    <Grid item xs={5} md={5}><Typography variant="body2">₩{Math.round(analytics.totalVolumes.bithumb * EXCHANGE_FEE_RATES.BITHUMB).toLocaleString()}</Typography></Grid>
                                </Grid>
                                <Grid container sx={{ color: '#666', mb: 0.5 }}>
                                    <Grid item xs={3} md={2}><Typography variant="body2">Binancef</Typography></Grid>
                                    <Grid item xs={4} md={3}><Typography variant="body2">${(Math.round(analytics.totalVolumes.binancef * 100) / 100).toLocaleString()}</Typography></Grid>
                                    <Grid item xs={0} md={2}><Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>{EXCHANGE_FEE_RATES.BINANCEF * 100}%</Typography></Grid>
                                    <Grid item xs={5} md={5}><Typography variant="body2">${(Math.round(analytics.totalVolumes.binancef * EXCHANGE_FEE_RATES.BINANCEF * 100) / 100).toLocaleString()}</Typography></Grid>
                                </Grid>
                                <Grid container sx={{ color: '#666', mb: 0.5 }}>
                                    <Grid item xs={3} md={2}><Typography variant="body2">Bybit</Typography></Grid>
                                    <Grid item xs={4} md={3}><Typography variant="body2">${(Math.round(analytics.totalVolumes.bybit * 100) / 100).toLocaleString()}</Typography></Grid>
                                    <Grid item xs={0} md={2}><Typography variant="body2" sx={{ display: { xs: 'none', md: 'block' } }}>{EXCHANGE_FEE_RATES.BYBIT * 100}%</Typography></Grid>
                                    <Grid item xs={5} md={5}><Typography variant="body2">${(Math.round(analytics.totalVolumes.bybit * EXCHANGE_FEE_RATES.BYBIT * 100) / 100).toLocaleString()}</Typography></Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                    </Grid>

                    {selectedPortfolio && portfolios.find(portfolio => portfolio.portfolioId === selectedPortfolio) && (
                        <OrdersTable 
                            orders={portfolios.find(portfolio => portfolio.portfolioId === selectedPortfolio).orders} 
                            analytics={analytics}
                        />
                    )}
                </Box>
            </Box>
        </Container>
    );
};

export { OrderAnalytics as default };
